import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  FrontendLabelingStrategy,
  LabelingStrategy,
  RegistryLabelingStrategy,
  RegistryPreferences,
  RegistrySummary,
} from 'constants/RegistryConstants';
import preferences from 'Preferences';

export function getLabelingStrategyPrefKey(tagSchemaIds: Array<string>): string {
  /**
   * Generate a string key from the combination of tagSchemaIds
   * Naming option preferences are stored on a per-combination basis as naming schemes can differ by schema,
   * and users may register files from more than one schema at a time.
   */
  // eslint-disable-next-line no-restricted-syntax
  return `${RegistryPreferences.LABELING_STRATEGY_NAMESPACE}${tagSchemaIds.sort().toString()}`;
}

// Keep in sync with `DEFAULT_NAME_TEMPLATE_LABELING_STRATEGY` on the backend
export const DEFAULT_NAME_TEMPLATE_LABELING_STRATEGY = RegistryLabelingStrategy.REPLACE_NAMES_FROM_PARTS;

export function getDefaultLabelingStrategy<T extends LabelingStrategy | RegistryLabelingStrategy>(
  labelingStrategies: Array<T>,
  tagSchemaIds?: Array<string>
): T {
  if (tagSchemaIds) {
    const preferredLabelingStrategy = preferences.get(getLabelingStrategyPrefKey(tagSchemaIds));
    if (preferredLabelingStrategy && labelingStrategies.includes(preferredLabelingStrategy)) {
      return preferredLabelingStrategy;
    }
  }
  if (labelingStrategies.includes(DEFAULT_NAME_TEMPLATE_LABELING_STRATEGY as T)) {
    return DEFAULT_NAME_TEMPLATE_LABELING_STRATEGY as T;
  }
  return labelingStrategies[0];
}

/**
 * Filters valid labeling strategies based on configs and other settings. If no options are valid and a
 * default option exists, return the default option.
 */
export function filterLabelingStrategies(
  availableLabelingStrategies: Array<LabelingStrategy>,
  {labelingStrategies}: {labelingStrategies: Array<LabelingStrategy>},
  registry?: RegistrySummary | null
): Array<LabelingStrategy> {
  const validLabelingStrategies = availableLabelingStrategies.filter((labelingStrategy) => {
    // Handle CONVERT_IDS specially since it isn't currently supported in the backend and should
    // always be available to academic registries.
    if (registry && labelingStrategy === FrontendLabelingStrategy.CONVERT_IDS) {
      return !registry.disablePersonalCollections;
    }
    return labelingStrategies.includes(labelingStrategy as RegistryLabelingStrategy);
  });
  assert(validLabelingStrategies.length >= 1, 'Expected at least one labeling strategy to be valid');
  return validLabelingStrategies;
}

/**
 * Returns registration labeling strategies enabled on the deploy.
 */
export function getEnabledLabelingStrategies(): Array<RegistryLabelingStrategy> {
  return _.compact([
    RegistryLabelingStrategy.NEW_IDS,
    RegistryLabelingStrategy.IDS_FROM_NAMES,
    RegistryLabelingStrategy.DELETE_NAMES,
    RegistryLabelingStrategy.SET_FROM_NAME_PARTS,
    RegistryLabelingStrategy.REPLACE_NAMES_FROM_PARTS,
    BenchlingConfig.ENABLE_REGISTRY_LABELING_STRATEGY_REPLACE_ID_AND_NAME_FROM_PARTS &&
      RegistryLabelingStrategy.REPLACE_ID_AND_NAME_FROM_PARTS,
    BenchlingConfig.ENABLE_REGISTRY_LABELING_STRATEGY_KEEP_NAMES && RegistryLabelingStrategy.KEEP_NAMES,
  ]);
}

export function getInitialLabelingStrategies(): Array<RegistryLabelingStrategy> {
  // Tag schemas cannot be created with name templates, so disallow those strategies initially.
  // eslint-disable-next-line no-restricted-syntax
  return _.without(
    getEnabledLabelingStrategies(),
    RegistryLabelingStrategy.SET_FROM_NAME_PARTS,
    RegistryLabelingStrategy.REPLACE_NAMES_FROM_PARTS,
    RegistryLabelingStrategy.REPLACE_ID_AND_NAME_FROM_PARTS
  );
}
